import { Wallet } from "lucide-react";
import type { WalletData } from "./types";
import { useTranslations } from "@/hooks/use-translations";

interface BalanceDisplayProps {
  walletData: WalletData | null;
  isLoadingWallet: boolean;
  currency: string;
  pair: string;
  marketPrice: string;
}

export default function BalanceDisplay({
  walletData,
  isLoadingWallet,
  currency,
  pair,
  marketPrice,
}: BalanceDisplayProps) {
  const t = useTranslations("trade/components/trading/spot/balance-display");
  // Format currency based on type
  const formatCurrency = (value: number, currencyType: string) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue === null || numValue === undefined) return "0";
    if (currencyType.includes("BTC")) return numValue.toFixed(8);
    if (currencyType.includes("ETH")) return numValue.toFixed(6);
    return numValue.toFixed(2);
  };

  // Get the actual currency and pair balances from passkey wallet
  const currencyBalance = walletData?.currencyBalance || 0;
  const pairBalance = walletData?.pairBalance || 0;

  // For blockchain wallet, display OTC and USDO instead of currency/pair
  const displayCurrency = "OTC";
  const displayPair = "USDO";

  return (
    <div className="flex items-center justify-between px-3 py-2 bg-muted/50 dark:bg-zinc-900/50 border-b border-border dark:border-zinc-800">
      <div className="flex items-center text-xs text-muted-foreground dark:text-zinc-400">
        <Wallet className="h-3.5 w-3.5 mr-1.5 text-muted-foreground/70 dark:text-zinc-500" />
        <span>{t("available")}</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {isLoadingWallet ? (
          <span className="text-foreground dark:text-zinc-300 animate-pulse">
            {t("Loading")}.
          </span>
        ) : (
          <>
            <span className="text-foreground dark:text-zinc-300">
              {formatCurrency(currencyBalance, displayCurrency)} {displayCurrency}
            </span>
            <span className="text-muted-foreground dark:text-zinc-500">
              {t("_")}
            </span>
            <span className="text-foreground dark:text-zinc-300">
              {formatCurrency(pairBalance, displayPair)} {displayPair}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
